/**
 * Primitive enums and unions shared across domain entities.
 *
 * Ported from legacy `frontend/src/types.ts`. Values match the backend DB
 * constraints — do NOT widen or narrow without a coordinated backend change.
 *
 * Zod schemas are authored first so backend response validation has a single
 * source of truth; TypeScript types are inferred from them via `z.infer`.
 */

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Postgres NUMERIC coercion
//
// Postgres NUMERIC columns (pg library default: `parseInt8` off) serialize as
// strings over the wire — e.g. "4.000", "46.27231092", "10.00". Coerce them
// to JS numbers at the schema boundary so every downstream consumer can rely
// on `typeof x === 'number'`. Non-numeric strings still fail validation.
// ---------------------------------------------------------------------------

export const pgNumeric = z.coerce
  .number()
  .refine((v) => !Number.isNaN(v), 'Expected numeric value');

// ---------------------------------------------------------------------------
// Measurement + quantity units
//
// T36.2 / ADR-015 — the old defined-but-unused `measurementUnitSchema` (three
// disagreeing vocabularies) is retired. The single canonical packing vocabulary
// + its pure normalizer now live in `./packing` (`CANONICAL_UNITS`,
// `normalizePacking`). Import the canonical units from there.
// ---------------------------------------------------------------------------

export const unitTypeSchema = z.enum(['count', 'weight', 'volume']);
export type UnitType = z.infer<typeof unitTypeSchema>;

// ---------------------------------------------------------------------------
// Currency + pricing
// ---------------------------------------------------------------------------

export const priceCurrencySchema = z.enum(['CAD', 'USD', 'EUR', 'GBP', 'MXN']);
export type PriceCurrency = z.infer<typeof priceCurrencySchema>;

// ---------------------------------------------------------------------------
// Availability / deal / offer semantics
// ---------------------------------------------------------------------------

export const productAvailabilitySchema = z.enum([
  'in_stock',
  'low_stock',
  'out_of_stock',
  'unknown',
]);
export type ProductAvailability = z.infer<typeof productAvailabilitySchema>;

export const dealTypeSchema = z.enum([
  'percent_off',
  'amount_off',
  'bogo',
  'multi_buy',
  'clearance',
]);
export type DealType = z.infer<typeof dealTypeSchema>;

// ---------------------------------------------------------------------------
// Shopping list
// ---------------------------------------------------------------------------

export const shoppingListTypeSchema = z.enum(['CONTINUOUS', 'EVENT']);
export type ShoppingListType = z.infer<typeof shoppingListTypeSchema>;

export const shoppingListStatusSchema = z.enum(['ACTIVE', 'ARCHIVED']);
export type ShoppingListStatus = z.infer<typeof shoppingListStatusSchema>;

export const shoppingListItemStatusSchema = z.enum(['pending', 'purchased', 'deferred']);
export type ShoppingListItemStatus = z.infer<typeof shoppingListItemStatusSchema>;

// ---------------------------------------------------------------------------
// Household membership role
// ---------------------------------------------------------------------------

export const householdRoleSchema = z.enum(['manager', 'member']);
export type HouseholdRole = z.infer<typeof householdRoleSchema>;
